'use client';

import useTranslation from 'next-translate/useTranslation';
import { Breadcrumbs, Container, Heading, PageContent } from '@stereoagency/ui';
import { defaultLanguage } from '@stereoagency/constants';
import { ReactNode, useContext, useRef } from 'react';
import flush from 'just-flush';
import ResearchProjectRow from '../GroupOrPlatformProjectRow.client';
import ResearchPlatformDiseases from '../GroupOrPlatformDiseases.client';
import ResearchPlatformPageNews from '../GroupOrPlatformNewsGrid.client';
import {
  researchPlatformArticlesQuery,
  researchPlatformJobsQuery,
  researchPlatformPageQuery,
  researchPlatformProjectsQuery
} from './researchPlatformPageQuery';
import ResearchPlatformPageHero from './ResearchPlatformPageHero.client';
import ResearchPlatformPageSidebar from '../GroupOrPlatformSidebar.client';
import ResearchPlatformPagePubmed from '../GroupOrPlatformPubmedListing.client';
import ResearchPlatformPageJobs from '../GroupOrPlatformJobs.client';
import { SimpleBlockContent } from '@/components/sanity/client';
import { createDocumentURL, findInKeyArray } from '@/utils';
import useActiveSectionIndex from '@/hooks/useActiveSectionIndex';
import { SpecialPagesContext } from '@/components/context';
import { diseasesForResearchPlatformQuery } from './diseasesForResearchPlatformQuery';
import GroupOrPlatformDepartmentsSection, {
  SingleProfile
} from '../GroupOrPlatformDepartmentsSection.client';

type Props = {
  lang: string;
  researchPlatform: (typeof researchPlatformPageQuery)[1];
  diseases: (typeof diseasesForResearchPlatformQuery)[1];
  projects?: (typeof researchPlatformProjectsQuery)[1]['projects'];
  articles?: (typeof researchPlatformArticlesQuery)[1]['articles'];
  jobs?: (typeof researchPlatformJobsQuery)[1]['jobs'];
  researchPlatformLeaderProfile?: SingleProfile;
  departmentProfiles?: SingleProfile[];
  aboutContent?: ReactNode;
  researchContent?: ReactNode;
  bioContent?: ReactNode;
  teamContent?: ReactNode;
};

export default function ResearchPlatformPage({
  lang,
  researchPlatform,
  diseases,
  projects,
  articles,
  jobs,
  researchPlatformLeaderProfile,
  departmentProfiles,
  aboutContent,
  researchContent,
  bioContent,
  teamContent
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeId = useActiveSectionIndex(containerRef);
  const { pages } = useContext(SpecialPagesContext);
  const { t } = useTranslation();

  const title = researchPlatform.title
    ? findInKeyArray(researchPlatform.title, [lang, defaultLanguage.id])
    : null;

  const introduction = researchPlatform.introduction
    ? findInKeyArray(researchPlatform.introduction, [lang, defaultLanguage.id])
    : null;

  const departments = researchPlatform.departments
    ? findInKeyArray(researchPlatform.departments, [lang, defaultLanguage.id])
    : null;

  const hasResearchContent = Boolean(
    researchContent || diseases.length || projects?.length
  );
  const hasBioContent = !!bioContent;
  const hasTeamContent = Boolean(!!departments?.value?.length || teamContent);
  const hasNews = !!articles?.length;
  const hasJobs = !!jobs?.length;

  const breadcrumbActions = flush([
    pages.research_group_listing_page?.href
      ? {
          href: pages.research_group_listing_page.href,
          label: pages.research_group_listing_page.title
        }
      : null,
    researchPlatform?.slug?.current
      ? {
          href: createDocumentURL(
            lang,
            'researchPlatform',
            researchPlatform.slug.current
          ),
          label: title?.value ?? ''
        }
      : null
  ]);

  const departmentsInfo = departments?.value ?? [];

  return (
    <>
      <Container>
        <Breadcrumbs actions={breadcrumbActions} />
      </Container>

      <ResearchPlatformPageHero
        lang={lang}
        researchPlatform={researchPlatform}
        researchPlatformLeaderProfile={researchPlatformLeaderProfile}
      />

      <Container ref={containerRef} className="[&_h2]:text-link-fg-base">
        <div className="grid gap-6 grid-cols-1 grid-flow-row auto-rows-max md:grid-cols-[18rem_1fr] md:gap-10 lg:gap-12 lg:grid-cols-[22rem_1fr]">
          <div className="grid grid-cols-1 grid-rows-1 items-start">
            {!!researchPlatformLeaderProfile && (
              <ResearchPlatformPageSidebar
                activeId={activeId}
                profile={researchPlatformLeaderProfile}
                hasPublications={
                  !!researchPlatformLeaderProfile?.pubmedArticles?.length
                }
                hasResearchContent={hasResearchContent}
                hasTeamContent={hasTeamContent}
                hasNews={hasNews}
                hasJobs={hasJobs}
              />
            )}
          </div>
          <PageContent className="my-0 md:my-0 md:col-start-2">
            {!!introduction?.value && (
              <div id="about">
                <Heading as="h3" variant="h3" className="font-body font-normal">
                  <SimpleBlockContent value={introduction.value} />
                </Heading>
              </div>
            )}

            {aboutContent}

            {hasResearchContent && (
              <>
                <Heading as="h2" variant="h2">
                  {t('research-group-or-platform:sections.research.title')}
                </Heading>

                {researchContent}

                {!!projects?.length && (
                  <div>
                    <Heading as="h3" variant="h3" className="mb-6">
                      {t(
                        'research-group-or-platform:sections.research.projects'
                      )}
                    </Heading>
                    <div className="grid grid-cols-1 grid-flow-row auto-rows-max gap-2">
                      {projects.map((project) => (
                        <ResearchProjectRow
                          key={project._id}
                          project={project}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {!!diseases?.length && (
                  <div>
                    <Heading as="h3" variant="h3" className="mb-6">
                      {t(
                        'research-group-or-platform:sections.research.diseases'
                      )}
                    </Heading>
                    <ResearchPlatformDiseases diseases={diseases} />
                  </div>
                )}
              </>
            )}

            {hasBioContent && (
              <>
                <Heading as="h2" variant="h2">
                  {t('research-group-or-platform:sections.bio.title')}
                </Heading>

                {bioContent}
              </>
            )}

            {hasTeamContent && (
              <>
                <Heading id="team" as="h2" variant="h2">
                  {t('research-group-or-platform:sections.team.title')}
                </Heading>

                <GroupOrPlatformDepartmentsSection
                  departments={departmentsInfo}
                  departmentProfiles={departmentProfiles}
                  leaderProfile={researchPlatformLeaderProfile}
                />

                {teamContent}
              </>
            )}

            <ResearchPlatformPagePubmed
              query={researchPlatformLeaderProfile?.pubmedQuery}
              articles={researchPlatformLeaderProfile?.pubmedArticles}
            />

            <ResearchPlatformPageNews articles={articles} />

            <ResearchPlatformPageJobs jobs={jobs} />
          </PageContent>
        </div>
      </Container>
    </>
  );
}
